// Textos de una fila del listado de promos: vigencia, presupuesto y palancas.
import { mxDay } from '@/lib/dateHelpers';
import { describeValidityCountdown } from '@/shared/patterns/Lifecycle';
import { formatPromotionValidity } from '@/shared/patterns/Promotions';
import type {
    AccessoryDiscountLever,
    GiftLever,
    SalePromotionLevers,
    SalePromotionListItem,
} from '../api/promotionsContract';
import { LEVER_FIELDS, LEVER_LABEL } from './promotionsFormat';
import { ACCESSORY_SUBCATEGORY_LABEL } from './salePromotionFormOptions';

const wholePesos = new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0,
});
const withCents = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });

/** Sin centavos cuando son exactos; con los dos dígitos cuando no. */
export function formatCents(cents: number): string {
    return (cents % 100 === 0 ? wholePesos : withCents).format(cents / 100);
}

/** Sin inicio arranca al publicarse; sin fin es permanente. */
export function formatValidity(
    promo: Pick<SalePromotionListItem, 'validFrom' | 'validUntil'>,
): string {
    return formatPromotionValidity(promo.validFrom, promo.validUntil);
}

/** La vigencia de la lista en una línea: «Vence en 8 días», «Permanente», «Arranca mañana». */
export function formatShortValidity(
    promo: Pick<SalePromotionListItem, 'validFrom' | 'validUntil'>,
    now: Date,
): string {
    return describeValidityCountdown(
        promo.validFrom,
        promo.validUntil,
        mxDay(now.toISOString()),
        now,
    );
}

/** Si la promo se suma con otras en el producto, para el `title` del estado. */
export function describeStackable(stackable: boolean | undefined): string | undefined {
    if (stackable === undefined) return undefined;
    return stackable
        ? 'Acumulable: se suma con otras promos en el mismo producto'
        : 'Va sola: una por producto, no se suma con otras que van solas';
}

/** «$730 de $5,000», o «Sin tope» cuando la promo no tiene presupuesto. */
export function formatBudget(
    promo: Pick<SalePromotionListItem, 'budgetConsumedCents' | 'budgetMaxCents'>,
): string {
    if (promo.budgetMaxCents === null) return 'Sin tope';
    return `${formatCents(promo.budgetConsumedCents)} de ${formatCents(promo.budgetMaxCents)}`;
}

/** Lo consumido en pesos redondos y, si hay tope, «de $50,000» como segundo renglón. */
export function formatConsumed(
    promo: Pick<SalePromotionListItem, 'budgetConsumedCents' | 'budgetMaxCents'>,
): { consumed: string; cap: string | null } {
    return {
        consumed: wholePesos.format(promo.budgetConsumedCents / 100),
        cap:
            promo.budgetMaxCents === null
                ? null
                : `de ${wholePesos.format(promo.budgetMaxCents / 100)}`,
    };
}

const percentFormat = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 2 });

type CappedDiscount = NonNullable<SalePromotionLevers['deviceDiscount']> | AccessoryDiscountLever;

/** El valor del descuento: «$500» o «10 %»; el tope solo se nombra en los de %. */
function discountParts(lever: CappedDiscount): { value: string; cap: string } {
    const { type, amountCents, percentBps, maxAmountCents } = lever as Partial<CappedDiscount>;
    const cap = maxAmountCents == null ? '' : ` hasta ${formatCents(maxAmountCents)}`;
    if (type === 'AMOUNT') return { value: formatCents(amountCents ?? 0), cap: '' };
    return { value: `${percentFormat.format((percentBps ?? 0) / 100)} %`, cap };
}

/** «$500 de descuento», «10 % de descuento» o «10 % hasta $300». */
export function deviceDiscountLabel(lever: NonNullable<SalePromotionLevers['deviceDiscount']>) {
    const { value, cap } = discountParts(lever);
    return cap ? `${value}${cap}` : `${value} de descuento`;
}

/** «$200 en accesorios» o «10 % en accesorios hasta $300»; sin `type` es %. */
export function accessoryDiscountLabel(lever: AccessoryDiscountLever) {
    const { value, cap } = discountParts(lever);
    return `${value} en accesorios${cap}`;
}

function leverLabel(levers: SalePromotionLevers, field: keyof SalePromotionLevers): string {
    if (field === 'gift') return giftLabel(levers.gift);
    if (field === 'deviceDiscount' && levers.deviceDiscount)
        return deviceDiscountLabel(levers.deviceDiscount);
    if (field === 'accessoryDiscount' && levers.accessoryDiscount)
        return accessoryDiscountLabel(levers.accessoryDiscount);
    return LEVER_LABEL[field];
}

/** El regalo por gama suma su subcategoría a la etiqueta. */
function giftLabel(gift: GiftLever | null): string {
    if (gift?.accessorySubcategory == null) return LEVER_LABEL.gift;
    return `${LEVER_LABEL.gift}: ${ACCESSORY_SUBCATEGORY_LABEL[gift.accessorySubcategory]}`;
}

export function formatLevers(promo: Pick<SalePromotionListItem, 'levers'>): string {
    const labels = LEVER_FIELDS.filter((field) => promo.levers[field] != null).map((field) =>
        leverLabel(promo.levers, field),
    );
    return labels.length === 0 ? 'Sin palancas' : labels.join(' · ');
}
